import { useState } from "react";
import { Twitter, Facebook, Linkedin, Link2, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

// Custom WhatsApp glyph (lucide has no whatsapp icon)
function WhatsAppIcon({ size = 14, strokeWidth = 1.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 21l1.65-4.05A9 9 0 1 1 8.05 20.35L3 21z" />
      <path d="M9 10c.5 2 2 3.5 4 4l1.2-1.2a1 1 0 0 1 1-.25l2.3.75a1 1 0 0 1 .7 1v2a1 1 0 0 1-1 1A9 9 0 0 1 8 9a1 1 0 0 1 1-1h2a1 1 0 0 1 1 .7l.75 2.3a1 1 0 0 1-.25 1L9 10z" />
    </svg>
  );
}

const buildUrl = (platform, url, text) => {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  switch (platform) {
    case "twitter":   return `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    case "facebook":  return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case "linkedin":  return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    case "whatsapp":  return `https://api.whatsapp.com/send?text=${t}%20${u}`;
    default:          return url;
  }
};

export default function SocialShare({ title, text, label = "Partager ce site", shareUrl: shareUrlProp }) {
  const { lang } = useLang();
  const en = lang === "en";
  const [copied, setCopied] = useState(false);
  const baseUrl = shareUrlProp || (typeof window !== "undefined" ? window.location.href.split("?")[0] : "");
  const url = en && baseUrl ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}lang=en` : baseUrl;
  const share = text || title || "Le Fil du Midi · Canal'Art 2026";
  const displayLabel = en && label === "Partager ce site" ? "Share this site" : label;

  const onShare = (platform) => (e) => {
    e.preventDefault();
    const shareUrl = buildUrl(platform, url, share);
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=640,height=520");
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* silent */
    }
  };

  const items = [
    { key: "twitter",  Icon: Twitter,      label: "Twitter / X" },
    { key: "facebook", Icon: Facebook,     label: "Facebook" },
    { key: "linkedin", Icon: Linkedin,     label: "LinkedIn" },
    { key: "whatsapp", Icon: WhatsAppIcon, label: "WhatsApp" },
  ];

  return (
    <div className="border-y border-fg/15 py-6 flex flex-wrap items-center justify-between gap-4" data-testid="social-share">
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">§ {displayLabel}</div>
      <div className="flex items-center gap-2">
        {items.map(({ key, Icon, label }) => (
          <a
            key={key}
            href="#share"
            onClick={onShare(key)}
            aria-label={`Partager sur ${label}`}
            data-testid={`share-${key}`}
            className="w-10 h-10 border border-fg/25 flex items-center justify-center text-fg/70 hover:text-bg hover:bg-cinabre hover:border-cinabre transition-colors"
          >
            <Icon size={14} strokeWidth={1.4} />
          </a>
        ))}
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copier le lien"
          data-testid="share-copy"
          className="w-10 h-10 border border-fg/25 flex items-center justify-center text-fg/70 hover:text-bg hover:bg-fg hover:border-fg transition-colors"
        >
          {copied ? <Check size={14} strokeWidth={1.6} /> : <Link2 size={14} strokeWidth={1.4} />}
        </button>
      </div>
    </div>
  );
}
